import React, { useState, useEffect, useRef } from 'react';
import ProductCard from './ProductCard';
import { Product } from '../api/products';

interface ProductGridProps {
    products: Product[];
    loading?: boolean;
    onQuickOrder: (product: Product) => void;
    gridClassName?: string;
    enableAutoZoom?: boolean;
    viewMode?: 'grid' | 'list';
}

const ProductGrid: React.FC<ProductGridProps> = ({ products, loading, onQuickOrder, gridClassName, enableAutoZoom = false, viewMode = 'grid' }) => {
    const defaultGridClass = "grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-8";
    const finalGridClass = gridClassName || defaultGridClass;

    // Auto-Focus Logic
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [isInteractionPaused, setIsInteractionPaused] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [passCount, setPassCount] = useState(0);

    useEffect(() => {
        if (!enableAutoZoom || isFinished || isInteractionPaused) return;

        const interval = setInterval(() => {
            setFocusedIndex((prev) => {
                const next = prev + 1;
                if (next >= products.length) {
                    if (passCount >= 1) { // 2 passes
                        setIsFinished(true);
                        return prev;
                    }
                    setPassCount(p => p + 1);
                    return 0;
                }
                return next;
            });
        }, 3800);

        return () => clearInterval(interval);
    }, [enableAutoZoom, isFinished, isInteractionPaused, products.length, passCount]);

    const handleMouseEnter = (index: number) => {
        if (!enableAutoZoom) return;
        setIsInteractionPaused(true);
        setFocusedIndex(index);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };

    const handleMouseLeave = () => {
        if (!enableAutoZoom) return;
        if (!isFinished) {
            pauseTimeoutRef.current = setTimeout(() => {
                setIsInteractionPaused(false);
            }, 2000);
        }
    };

    if (loading) {
        return (
            <div className={finalGridClass}>
                {[...Array(8)].map((_, i) => (
                    <div key={i} className="bg-white rounded-3xl shadow-sm p-4 h-96 animate-pulse">
                        <div className="bg-neutral-200 h-48 rounded-2xl mb-4"></div>
                        <div className="bg-neutral-200 h-4 w-1/3 mb-2 rounded"></div>
                        <div className="bg-neutral-200 h-6 w-3/4 mb-4 rounded"></div>
                        <div className="bg-neutral-200 h-10 w-full rounded-full mt-auto"></div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className={finalGridClass}>
            {products.map((product, index) => (
                <div
                    key={product.id}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                >
                    <ProductCard
                        product={product}
                        onQuickOrder={onQuickOrder}
                        isFocused={enableAutoZoom && index === focusedIndex && !isInteractionPaused && !isFinished}
                        viewMode={viewMode}
                    />
                </div>
            ))}
        </div>
    );
};

export default ProductGrid;
